import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Building2 } from 'lucide-react';

export type SmartLogoVariant = 'nav' | 'card' | 'inline' | 'profile' | 'preview';

interface SmartLogoProps {
  src: string | null | undefined;
  alt: string;
  variant: SmartLogoVariant;
  fallbackIcon?: React.ReactNode;
  fallbackInitial?: string;
  className?: string;
}

const variantStyles: Record<SmartLogoVariant, {
  container: string;
  image: string;
  fallback: string;
}> = {
  nav: {
    container: 'h-[clamp(2rem,3vw,2.5rem)] px-2 rounded-lg',
    image: 'h-[clamp(1.25rem,2vw,1.75rem)] w-auto max-w-[80px] object-contain',
    fallback: 'w-8 h-8 text-sm',
  },
  card: {
    container: 'w-full h-full rounded-none p-4',
    image: 'max-w-[80%] max-h-[80%] object-contain',
    fallback: 'w-16 h-16 text-2xl',
  },
  inline: {
    container: 'w-10 h-10 rounded-lg',
    image: 'w-full h-full object-contain p-1',
    fallback: 'w-10 h-10 text-sm',
  },
  profile: {
    container: 'w-20 h-20 rounded-xl',
    image: 'w-full h-full object-contain p-1.5',
    fallback: 'w-20 h-20 text-2xl',
  },
  preview: {
    container: 'w-20 h-20 rounded-xl',
    image: 'w-full h-full object-contain p-1',
    fallback: 'w-20 h-20 text-xl',
  },
};

/**
 * SmartLogo - A unified component for rendering company logos consistently
 * across light and dark modes, regardless of logo background type.
 * 
 * Uses a theme-adaptive neutral container:
 * - Light: white bg with subtle border (white-bg logos blend, colored logos pop)
 * - Dark: slate-800 bg with subtle border (provides contrast for all logo types)
 */
const SmartLogo: React.FC<SmartLogoProps> = ({
  src,
  alt,
  variant,
  fallbackIcon,
  fallbackInitial,
  className,
}) => {
  const [hasError, setHasError] = useState(false);
  const styles = variantStyles[variant];

  const containerBase = cn(
    'flex items-center justify-center overflow-hidden',
    'bg-background border border-border/60 shadow-sm',
    'dark:bg-foreground dark:border-border/30 dark:shadow-lg',
    styles.container,
    className
  );

  // Show fallback if no src or image errored
  if (!src || hasError) {
    return (
      <div className={containerBase}>
        {fallbackIcon || (
          fallbackInitial ? (
            <span className={cn(
              'flex items-center justify-center rounded-lg',
              'bg-primary/10 text-primary font-semibold',
              styles.fallback
            )}>
              {fallbackInitial}
            </span>
          ) : (
            <Building2 className={cn(
              'text-muted-foreground',
              variant === 'card' ? 'w-10 h-10' :
              variant === 'profile' || variant === 'preview' ? 'w-8 h-8' :
              'w-5 h-5'
            )} />
          )
        )}
      </div>
    );
  }

  return (
    <div className={containerBase}>
      <img
        src={src}
        alt={alt}
        className={styles.image}
        onError={() => setHasError(true)}
      />
    </div>
  );
};

export default SmartLogo;
